import { type OrderDetails, CONVEYANCE_TYPES, type DeliveryAddress } from '@koala/sdk';
import { useEffect, useState } from 'react';
import {
  StyledReorderFormContent,
  StyledConveyanceBadge,
  StyledOrderNumber,
  StyledLocationLabel,
  StyledItemsSectionLabel,
  StyledItemsList,
  StyledItem,
  StyledItemName,
  StyledItemQuantity,
  StyledReorderFooter,
  StyledAddToCartButton,
  StyledCancelButton,
  StyledDate,
  StyledReorderFormDate,
  StyledLocationContainer,
  StyledDeliveryAddress,
  StyledDeliveryLabel,
  StyledChangeAddressButton,
  StyledDeliveryError,
  StyledDeliveryContainer,
  StyledDeliveryLabelContainer,
  StyledDeliveryStatusText,
  StyledCheckMark,
  StyledValidateAddress,
  StyledExclamationTriangle,
  StyledGoogleAutocompleteWrapper,
  StyledAddShippingDetails,
  StyledExclamationCircle,
  StyledAddShippingDetailsLabel,
} from './styles';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { GoogleAutocomplete } from '@/components/autocomplete';
import { ERROR_MESSAGES } from '@/constants/events';
import { coverageErrorEventName, coverageErrorMessage } from '@/constants/coverageErrorCopy';
import { DATE_FORMAT } from '@/constants/dates';
import { API_FOR_CONVEYANCE_TYPES_SWAP } from '@/constants/global';
import { formatDate } from '@/utils/dates';
import { useLocation } from '@/features/locations/service';
import {
  addressCheckDeliveryCoverage,
  addressSetTimeWantedFields,
} from '@/features/menu-availability/address.service';
import { useSelector, useDispatch } from '@/redux';
import { useStrings } from '@/redux/cmsConfig';
import { selectConveyance } from '@/redux/conveyanceMode/reducer';
import globalActions from '@/redux/global/actions';
import * as ErrorReporter from '@/utils/errorReporter';
import { prepareErrorMessage } from '@/utils/global';
import { isValidZip } from '@/utils/locations';
import { safelyGetString } from '@/utils/stringHelpers';

interface Props {
  order: OrderDetails;
  submitting: boolean;
  onConfirm: (deliveryAddress: DeliveryAddress | null) => void;
  onClose: () => void;
}

interface GoogleDeliveryAddress {
  street_address: string;
  city: string;
  zip_code: string;
  state?: string | null;
  street_address_2?: string | null;
  phone_number?: string;
  special_instructions?: string;
}

export const ReorderForm = ({ order, submitting, onConfirm, onClose }: Props) => {
  const dispatch = useDispatch();
  const strings = useStrings();
  const { time_wanted } = useSelector(selectConveyance);

  const orderConveyance =
    API_FOR_CONVEYANCE_TYPES_SWAP[order.order_data.basket.conveyance_type.type] ||
    CONVEYANCE_TYPES.PICKUP;

  const isDeliveryOrder = orderConveyance === CONVEYANCE_TYPES.DELIVERY;
  const previousOrderDeliveryAddress =
    order.order_data.basket.conveyance_type.type === 'delivery'
      ? order.order_data.basket.conveyance_type.delivery_address
      : undefined;

  const { data: locationDetails, isSuccess: locationSuccess } = useLocation(
    isDeliveryOrder ? order.store_location?.id : undefined,
  );

  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [isPreviousAddress, setIsPreviousAddress] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddress | null>(null);

  const isConfirmDisabled = (isDeliveryOrder && !deliveryAddress) || submitting || validating;

  const displayAddress = deliveryAddress ?? previousOrderDeliveryAddress;
  const deliveryStreetAddress = displayAddress?.street_address_2
    ? `${displayAddress.street_address} ${displayAddress.street_address_2}`
    : displayAddress?.street_address;

  // Validate the order's delivery address when the modal opens
  useEffect(() => {
    if (
      isDeliveryOrder &&
      locationSuccess &&
      locationDetails?.data &&
      previousOrderDeliveryAddress
    ) {
      validateDeliveryAddress(previousOrderDeliveryAddress, true);
    }
  }, [locationSuccess]);

  async function validateDeliveryAddress(
    address: GoogleDeliveryAddress,
    isPreviousDeliveryAddress = false,
  ) {
    if (!locationDetails?.data) return;

    setIsPreviousAddress(isPreviousDeliveryAddress);
    setValidating(true);
    setDeliveryError(null);

    const fullAddress: DeliveryAddress = {
      street_address: address.street_address,
      city: address.city,
      zip_code: address.zip_code,
      state: address.state ?? undefined,
      street_address_2: address.street_address_2 ?? undefined,
      special_instructions: address.special_instructions ?? '',
      ...addressSetTimeWantedFields(time_wanted),
    };

    // Catch a blank/malformed ZIP before coverage/basket-validate. A missing
    // Google postal code otherwise reaches Koala as MalformedPayload (code 9)
    // on #/customer/zip and blocks reorder with a generic error (TEAM-14096).
    // This guard also runs on mount against the previous order's stored address,
    // so trim first (legacy/partial values may carry stray whitespace the
    // anchored regex would otherwise reject) and tailor the message to that case.
    if (!isValidZip((fullAddress.zip_code ?? '').trim())) {
      setDeliveryError(
        isPreviousDeliveryAddress
          ? 'The previous address is missing a ZIP code. Please enter a new delivery address below.'
          : "We couldn't read a ZIP code for that address. Please choose a more specific address that includes a ZIP or postal code.",
      );
      ErrorReporter.captureException(
        new Error('Reorder: delivery address missing a valid ZIP'),
        'info',
        {
          address: fullAddress,
          locationId: locationDetails.data.id,
        },
      );
      setValidating(false);
      return;
    }

    try {
      const response = await addressCheckDeliveryCoverage(locationDetails.data.id, fullAddress);

      if (response.can_deliver) {
        setDeliveryAddress(fullAddress);
      } else {
        // For a new address, read from the config-driven catalog so a brand's
        // customised wording is honoured here too (AC #12). `safelyGetString`
        // rather than the `<StringAccessor>` component used elsewhere for this
        // same key, because this is React state feeding `setDeliveryError`, not
        // rendered markup.
        const errorMessage = isPreviousDeliveryAddress
          ? 'The previous address is outside the delivery zone. Please enter a new delivery address below.'
          : safelyGetString(strings, 'locations.delivery_search_no_results_text');

        setDeliveryError(errorMessage);
        ErrorReporter.captureException(
          new Error('Reorder: Cannot deliver to this address'),
          'info',
          {
            address: fullAddress,
            locationId: locationDetails.data.id,
            eventName: ERROR_MESSAGES.CHECK_DELIVERY_COVERAGE_ERROR,
            details: response.message,
          },
        );
      }
    } catch (error) {
      // Third carrier of the old "try again in a few minutes" outage claim —
      // the ticket names only the other two, but AC #9 requires that string to
      // be unreachable, so it goes here as well.
      const errorMessage = coverageErrorMessage(error);
      setDeliveryError(errorMessage);
      dispatch(globalActions.displayErrorToast(errorMessage, true));
      const errorResponse = await prepareErrorMessage(null, error);
      ErrorReporter.captureException(error, 'error', {
        eventName: coverageErrorEventName(error),
        details: errorResponse,
      });
    } finally {
      setValidating(false);
    }
  }

  return (
    <StyledReorderFormContent>
      <StyledReorderFormDate>
        <StyledDate>
          {formatDate(order.order_data.order.ordered_at, DATE_FORMAT.WRITTEN_OUT_DATE)}
        </StyledDate>
        <StyledConveyanceBadge>{orderConveyance}</StyledConveyanceBadge>
        <StyledOrderNumber>#{order.order_data.order.order_number}</StyledOrderNumber>
      </StyledReorderFormDate>

      <StyledLocationContainer>
        <span role="img" aria-label="location">
          📍
        </span>

        <StyledLocationLabel>{order.store_location?.label}</StyledLocationLabel>
      </StyledLocationContainer>

      <StyledItemsSectionLabel>Items</StyledItemsSectionLabel>
      <StyledItemsList>
        {order.order_data.basket.basket_items.map((basketItem, idx) => (
          <StyledItem key={`${basketItem.id}-${idx}`}>
            <StyledItemName>{basketItem.product.name}</StyledItemName>
            <StyledItemQuantity>&times;{basketItem.quantity}</StyledItemQuantity>
          </StyledItem>
        ))}
      </StyledItemsList>

      {isDeliveryOrder && (
        <StyledDeliveryContainer>
          <StyledDeliveryLabelContainer>
            <StyledDeliveryLabel>DELIVERY DETAILS</StyledDeliveryLabel>

            {deliveryAddress && (
              <StyledChangeAddressButton type="button" onClick={() => setDeliveryAddress(null)}>
                Edit address
              </StyledChangeAddressButton>
            )}
          </StyledDeliveryLabelContainer>

          {deliveryError && (
            <StyledDeliveryError>
              <StyledExclamationTriangle title="Exclamation Triangle" />

              {deliveryError}
            </StyledDeliveryError>
          )}

          {isPreviousAddress && (
            <StyledDeliveryStatusText>
              <StyledCheckMark title="Check Mark" /> Address from previous order
            </StyledDeliveryStatusText>
          )}

          <StyledDeliveryLabel>DELIVERY ADDRESS</StyledDeliveryLabel>

          {deliveryAddress && !deliveryError && displayAddress && (
            <StyledDeliveryAddress>
              {deliveryStreetAddress}, {displayAddress.city}, {displayAddress.state}{' '}
              {displayAddress.zip_code}
            </StyledDeliveryAddress>
          )}

          {(deliveryError || !deliveryAddress) && locationDetails?.data && !validating && (
            <StyledGoogleAutocompleteWrapper>
              <GoogleAutocomplete
                hideLabel
                onUpdate={(address) => validateDeliveryAddress(address)}
              />

              <StyledAddShippingDetails>
                <StyledExclamationCircle title="Exclamation Circle" />

                <StyledAddShippingDetailsLabel>
                  Add building, suite, floor, or apartment details in <b>Delivery Instructions</b>{' '}
                  at checkout
                </StyledAddShippingDetailsLabel>
              </StyledAddShippingDetails>
            </StyledGoogleAutocompleteWrapper>
          )}

          {validating && (
            <StyledValidateAddress>Validating delivery address...</StyledValidateAddress>
          )}
        </StyledDeliveryContainer>
      )}

      <StyledReorderFooter>
        <StyledAddToCartButton
          data-css-override="StyledPrimaryButton"
          $submitting={submitting || validating}
          disabled={isConfirmDisabled}
          onClick={() => onConfirm(deliveryAddress)}
        >
          <StringAccessor accessor="reorders.add_to_cart_cta" />
        </StyledAddToCartButton>
        <StyledCancelButton type="button" onClick={onClose}>
          Cancel
        </StyledCancelButton>
      </StyledReorderFooter>
    </StyledReorderFormContent>
  );
};
